'use client';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { LuChevronRight, LuChevronDown } from 'react-icons/lu';
import { Button } from '@/core/ui/primitives/button';
import type { UiGroupedResult } from '@search/core/SearchTypes';
interface SearchResultsGroupedProps {
  results: UiGroupedResult[];
  selectedIndex: number;
  onMatchClick: (url: string) => void;
  query: string;
}
function escapeRegex(str: string) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
function highlightNodes(text: string, query: string): React.ReactNode[] {
  if (!query || !query.trim()) return [text];
  const terms = query
    .toLowerCase()
    .split(/\s+/)
    .filter((t) => t.length > 1)
    .map((t) => escapeRegex(t));
  if (terms.length === 0) return [text];
  const regex = new RegExp(`\\b(${terms.join('|')})\\b`, 'gi');
  const parts = text.split(regex);
  const out: React.ReactNode[] = [];
  for (let i = 0; i < parts.length; i++) {
    const chunk = parts[i];
    if (i % 2 === 1) {
      out.push(
        <mark key={i} className='bg-yellow-200 dark:bg-yellow-800 px-0.5 rounded'>
          {chunk}
        </mark>
      );
    } else if (chunk) {
      out.push(<React.Fragment key={i}>{chunk}</React.Fragment>);
    }
  }
  return out.length ? out : [text];
}
function getVaultColor(vaultSlug: string): string {
  const colors: Record<string, string> = {
    articles: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    projects: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    zenaxiom: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  };
  return colors[vaultSlug] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
}
export function SearchResultsGrouped({ results, selectedIndex, onMatchClick, query }: SearchResultsGroupedProps) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const toggle = (key: string) => setOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  const maxScore = Math.max(0, ...results.map((r) => (Number.isFinite(r.pageScore as any) ? Number(r.pageScore) : 0)));
  function labelForPage(score: number): {
    label: string;
    className: string;
  } {
    const ratio = maxScore > 0 ? score / maxScore : 0;
    const percent = Number.isFinite(ratio) ? Math.min(100, Math.max(0, ratio * 100)) : 0;
    if (percent >= 85) {
      return {
        label: 'Excellent',
        className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200',
      };
    }
    if (percent >= 60) {
      return { label: 'Strong', className: 'bg-lime-100 text-lime-800 dark:bg-lime-900 dark:text-lime-200' };
    }
    if (percent >= 35) {
      return { label: 'Good', className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' };
    }
    if (percent >= 15) {
      return { label: 'Fair', className: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' };
    }
    return { label: 'Related', className: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200' };
  }
  function formatScore(raw: number): string {
    if (!Number.isFinite(raw) || raw === 0) return '0';
    if (Math.abs(raw) >= 1) return raw.toFixed(2);
    if (Math.abs(raw) >= 0.01) return raw.toFixed(3);
    if (Math.abs(raw) >= 0.0001) return raw.toPrecision(2);
    return raw.toExponential(2);
  }
  const listRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  useEffect(() => {
    if (!Array.isArray(results) || results.length === 0) return;
    const target = itemRefs.current[selectedIndex];
    if (!target) return;
    try {
      target.scrollIntoView({ block: 'nearest' });
    } catch {
      // ignore
    }
  }, [results, selectedIndex]);
  return (
    <div ref={listRef} className='max-h-96 overflow-y-auto overflow-x-hidden w-full'>
      {results.map((group, index) => {
        const isSelected = index === selectedIndex;
        const vaultColor = getVaultColor(group.vaultSlug);
        // Use vaultId for key to avoid cross-org slug collisions
        const key = `${group.vaultId}:${group.pageUrl}`;
        const isOpen = !!open[key];
        const bestMatch = group.matches[0];
        const restMatches = group.matches.slice(1);
        const hiddenCount = Math.max(0, restMatches.length);
        return (
          <div
            key={key}
            ref={(el) => {
              itemRefs.current[index] = el;
            }}
            className={`w-full transition shadow-sm ${
              isSelected
                ? 'bg-blue-50/60 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800'
                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:shadow-md'
            }`}>
            <div
              className='p-4 py-2 cursor-pointer'
              onClick={() => {
                if (bestMatch) onMatchClick(bestMatch.url);
              }}>
              <div className='flex items-start gap-2'>
                <div className='flex-1 min-w-0'>
                  {/* Header */}
                  <div className='flex items-center gap-2 mb-2 flex-wrap min-w-0'>
                    <h3 className='font-semibold text-gray-900 dark:text-gray-100 truncate text-lg'>
                      {highlightNodes(group.pageTitle || group.relativePath, query)}
                    </h3>
                    {(() => {
                      const tag = labelForPage(group.pageScore);
                      return (
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${tag.className}`}>
                          {tag.label}
                          <span className='ml-1 text-[10px] opacity-60 align-middle font-mono'>
                            {formatScore(group.pageScore)}
                          </span>
                        </span>
                      );
                    })()}
                    <span className={`px-2 py-0.5 rounded-full text-xs ${vaultColor}`}>
                      {group.vaultName || group.vaultSlug}
                    </span>
                    {group.orgName && (
                      <span className='px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'>
                        {group.orgName}
                      </span>
                    )}
                  </div>

                  {/* Best match only (initial) */}
                  {bestMatch && (
                    <div className='mt-1'>
                      <div className='text-sm font-medium text-gray-900 dark:text-gray-100 block min-w-0 max-w-full overflow-hidden break-words line-clamp-2 leading-snug'>
                        {highlightNodes(bestMatch.heading || '', query)}
                      </div>
                      <div className='text-xs text-gray-600 dark:text-gray-300 line-clamp-2 mt-1 pb-1 overflow-hidden'>
                        {highlightNodes(bestMatch.snippet || '', query)}
                      </div>
                    </div>
                  )}

                  {/* Toggle to reveal other matches */}
                  {hiddenCount > 0 && (
                    <div className='mt-1'>
                      <Button
                        type='button'
                        variant='ghost'
                        size='sm'
                        className='w-full flex items-center justify-center gap-2 text-xs text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 py-1'
                        onClick={(e) => {
                          e.stopPropagation();
                          toggle(key);
                        }}
                        title={isOpen ? 'Hide other matches' : 'Show other matches'}>
                        <LuChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                        {isOpen ? 'Hide other matches' : `Show ${hiddenCount} more matches`}
                      </Button>
                    </div>
                  )}

                  {/* Expanded: list other matches with separators (not individually linked) */}
                  {isOpen && restMatches.length > 0 && (
                    <ul className='mt-2 border-t border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700 rounded'>
                      {restMatches.map((m) => (
                        <li key={m.id} className='py-2'>
                          <div className='text-[13px] font-medium text-gray-900 dark:text-gray-100 break-words line-clamp-2'>
                            {highlightNodes(m.heading || '', query)}
                          </div>
                          <div className='text-xs text-gray-600 dark:text-gray-300 line-clamp-2 mt-1 pb-1 overflow-hidden'>
                            {highlightNodes(m.snippet || '', query)}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
export default SearchResultsGrouped;
